import { Alert, AlertDescription, Button } from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';
import { formatDateTimeInPT } from '../../utils/dateUtils';

export const STALE_AFTER_MS = 30 * 60 * 60 * 1000;

export interface StaleDataBannerProps {
  builtAt: string | null;
  now: Date;
  refreshing: boolean;
  revalidating: boolean;
  refreshFailed: boolean;
  onRefresh: () => void;
}

/**
 * Offers a Refresh when the report data is over 30 h old, and says so when a
 * Refresh is running or failed. It never refreshes by itself.
 */
export default function StaleDataBanner({
  builtAt,
  now,
  refreshing,
  revalidating,
  refreshFailed,
  onRefresh,
}: StaleDataBannerProps) {
  const stale =
    builtAt !== null && now.getTime() - Date.parse(builtAt) > STALE_AFTER_MS;
  if (!stale && !refreshing && !refreshFailed) return null;

  const asOf = builtAt ? formatDateTimeInPT(builtAt) : null;
  const message = refreshing
    ? 'Refreshing…'
    : refreshFailed
      ? `Could not refresh.${asOf ? ` Data as of ${asOf}.` : ''}`
      : `This data is from ${asOf}.`;

  return (
    <Alert className='mb-3'>
      <AlertTriangle className='h-4 w-4' />
      <AlertDescription className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4'>
        <span>{message}</span>
        <Button
          size='sm'
          variant='outline'
          disabled={refreshing || revalidating}
          onClick={onRefresh}
        >
          Refresh
        </Button>
      </AlertDescription>
    </Alert>
  );
}
